import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';
import { logger } from '../../shared/utils/logger';
import {
  getTaxDeclarations,
  createTaxDeclaration,
  updateTaxDeclaration,
  deleteTaxDeclaration,
  listTaxDeclarationRecords,
  getTaxCalendar,
  getLiasseFiscale,
  getTVCP,
  generateTVCP,
  updateTVCPComponent,
  recalculateTVCP,
  exportTVCP,
  getTVCPAuditLog,
  getCurrentFiscalYear,
} from '../../shared/utils/comptabiliteService';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { FileText, AlertTriangle, CheckCircle, DollarSign, TrendingUp, FileSpreadsheet, Download, Plus, Search, Filter, ChevronDown, LayoutList, List, Shield, Calendar, Upload, Clock, LayoutGrid, Layers, Send, X, Receipt, Users, Briefcase, Building, FileCheck, Save, Printer, Calculator, ChevronUp, Award, RefreshCw, RefreshCcw, XCircle, BarChart3, Settings } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { mapComptabiliteStatusOptions, useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import * as XLSX from 'xlsx';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix } from '../../shared/utils/brandCurrency';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import '../../shared/styles/GPSTrackingDatePicker.css';
import {
  ZakatDeclarationForm,
  CorporateIncomeTaxForm,
  WithholdingTaxForm } from './utils/SaudiTaxDeclarationForms';
import {
  buildDefaultVatReturn,
  buildDefaultZakatReturn,
  buildDefaultCitReturn,
  buildDefaultWhtReturn,
  SAUDI_VAT_RATE_LABEL } from './utils/saudiTaxConfig';
import { fmtMoney, fmtDate, getAccountingLocale } from './utils/comptabiliteFormat';
import { useSettings } from '../../core/contexts/SettingsContext';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { useComptabiliteConfig } from './useComptabiliteConfig';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';


const DROPDOWN_PANEL_STYLE = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  zIndex: 99999,
  backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
  boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' };

function resolveTaxCalendarType(rawType = '') {
  const key = String(rawType || '').toLowerCase();
  if (!key) return 'vat';
  if (key.includes('zakat') || key.includes('zakah') || key.includes('زكاة')) return 'zakat';
  if (key.includes('wht') || key.includes('withhold') || key.includes('استقطاع')) return 'wht';
  if (key.includes('cit') || key.includes('income') || key.includes('irg') || key.includes('ibs') || key.includes('tap') || key.includes('دخل')) return 'cit';
  if (key.includes('vat') || key.includes('tva') || key.includes('g50') || key.includes('مضافة')) return 'vat';
  return key;
}

function normalizeTaxCalendarItem(row = {}) {
  const urgency = row.status || row.urgency || 'future';
  const status = urgency === 'overdue' ? 'urgent' : urgency;
  const type = resolveTaxCalendarType(row.type || row.tax_type || row.declaration_type);
  const dueRaw = row.date || row.due_date || '';
  const date = dueRaw ? String(dueRaw).slice(0, 10) : '';
  return {
    ...row,
    type,
    status,
    declaration: row.declaration || row.title || row.name || row.description || '',
    date,
  };
}

const DECLARATION_STATUS_DEFS = [
  { value: 'all', labelKey: 'status_all' },
  { value: 'urgent', labelKey: 'status_urgent' },
  { value: 'upcoming', labelKey: 'status_upcoming' },
  { value: 'future', labelKey: 'status_future' },
];

const DECL_RECORD_STATUS_DEFS = [
  { value: 'pending', labelKey: 'record_pending', icon: AlertTriangle, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  { value: 'submitted', labelKey: 'status_deposited', icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { value: 'draft', labelKey: 'record_draft', icon: Clock, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
];

const DECL_TYPE_SELECT_OPTIONS = [
  { value: 'vat', label: 'ضريبة القيمة المضافة (VAT)', isDefault: true },
  { value: 'zakat', label: 'الزكاة', isDefault: true },
  { value: 'cit', label: 'ضريبة الدخل (CIT)', isDefault: true },
  { value: 'wht', label: 'ضريبة الاستقطاع (WHT)', isDefault: true },
];

const DECL_PERIOD_SELECT_DEFS = [
  { value: '2025-01', labelKey: 'period_jan_2025', isDefault: true },
  { value: '2025-02', labelKey: 'period_feb_2025', isDefault: true },
  { value: '2025-03', labelKey: 'period_mar_2025', isDefault: true },
  { value: '2025-Q1', labelKey: 'period_q1_2025', isDefault: true },
];

const DECL_FORMAT_SELECT_DEFS = [
  { value: 'pdf', labelKey: 'format_pdf', isDefault: true },
  { value: 'excel', labelKey: 'format_excel', isDefault: true },
];

const TVCP_COMPONENT_DEFS = [
  { code: 'capital', labelKey: 'comp_capital', shortLabelKey: 'comp_capital_short' },
  { code: 'prime_emission', labelKey: 'comp_premium', shortLabelKey: 'comp_premium_short' },
  { code: 'reserves', labelKey: 'comp_reserves', shortLabelKey: 'comp_reserves_short' },
  { code: 'ecart_evaluation', labelKey: 'comp_eval_gap', shortLabelKey: 'comp_eval_gap_short' },
  { code: 'ecart_reevaluation', labelKey: 'comp_reval_gap', shortLabelKey: 'comp_reval_gap_short' },
  { code: 'report_nouveau', labelKey: 'comp_retained', shortLabelKey: 'comp_retained_short' },
  { code: 'resultat_exercice', labelKey: 'comp_result', shortLabelKey: 'comp_result_short' },
];

const TVCP_ROW_DEFS = [
  { key: 'solde_n2', labelKey: 'row_balance_n2', type: 'opening', editable: true, bold: true },
  { key: 'changement_methode_n1', labelKey: 'row_method_change_n1', type: 'change_n1', editable: true },
  { key: 'correction_erreurs_n1', labelKey: 'row_error_correction_n1', type: 'change_n1', editable: true },
  { key: 'reevaluation_immo_n1', labelKey: 'row_reval_immo_n1', type: 'change_n1', editable: true },
  { key: 'augmentation_capital_n1', labelKey: 'row_capital_increase_n1', type: 'change_n1', editable: true },
  { key: 'dividendes_payes_n1', labelKey: 'row_dividends_n1', type: 'change_n1', editable: true },
  { key: 'affectation_resultat_n1', labelKey: 'row_result_allocation_n1', type: 'change_n1', editable: true },
  { key: 'resultat_net_n1', labelKey: 'row_net_result_n1', type: 'change_n1', editable: true },
  { key: 'solde_n1', labelKey: 'row_balance_n1', type: 'calculated', editable: false, bold: true, color: 'blue' },
  { key: 'changement_methode_n', labelKey: 'row_method_change_n', type: 'change_n', editable: true },
  { key: 'correction_erreurs_n', labelKey: 'row_error_correction_n', type: 'change_n', editable: true },
  { key: 'reevaluation_immo_n', labelKey: 'row_reval_immo_n', type: 'change_n', editable: true },
  { key: 'augmentation_capital_n', labelKey: 'row_capital_increase_n', type: 'change_n', editable: true },
  { key: 'dividendes_payes_n', labelKey: 'row_dividends_n', type: 'change_n', editable: true },
  { key: 'affectation_resultat_n', labelKey: 'row_result_allocation_n', type: 'change_n', editable: true },
  { key: 'resultat_net_n', labelKey: 'row_net_result_n', type: 'change_n', editable: true },
  { key: 'solde_n', labelKey: 'row_balance_n', type: 'calculated', editable: false, bold: true, color: 'green' },
];

const FORM_CODE_SELECT_OPTIONS = [
  { value: 'VAT', label: 'إقرار ضريبة القيمة المضافة', isDefault: true },
  { value: 'VAT-Q', label: 'إقرار VAT — ربع سنوي', isDefault: true },
  { value: 'ZAKAT', label: 'إقرار الزكاة', isDefault: true },
  { value: 'CIT', label: 'إقرار ضريبة الدخل', isDefault: true },
  { value: 'WHT', label: 'إقرار ضريبة الاستقطاع', isDefault: true },
];

const EMPTY_TVA_FORM = {
  period: '',
  declarationType: 'monthly',
  tvaCollectee: '',
  tvaDeductible: '',
  notes: '' };

const EMPTY_NOUVELLE_DECL_FORM = {
  declarationCode: '',
  declarationType: '',
  period: '',
  fiscalYear: '',
  status: '',
  autoCalculate: '',
  draftMode: '',
  notes: '' };

const EMPTY_FORMULAIRE_FORM = {
  formCode: '',
  format: '',
  language: '',
  notes: '' };

const DEFAULT_TVA_SECTIONS = { periode: true, montants: false, notes: false };
const DEFAULT_NOUVELLE_SECTIONS = { typePeriode: true, identification: false, optionsCalcul: false, validation: false };
const DEFAULT_FORMULAIRE_SECTIONS = { selection: true, format: false, impression: false, notes: false };


// --- TVCPManager (inlined) ---
const FiscalModalShell = ({ open, onClose, title, Icon, iconClass = 'text-blue-400', children, footer, maxWidth = '1200px', zIndex = 99999 }) => {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4" style={{ zIndex }} onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            {Icon && <Icon className={`h-6 w-6 ${iconClass}`} />}
            {title}
          </h2>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-1">{children}</div>
        {footer && <div className="p-4 border-t border-white/10 flex justify-end gap-2 flex-wrap shrink-0">{footer}</div>}
      </motion.div>
    </div>,
    document.body
  );
};

const FiscalCollapsible = ({ icon: Icon, iconClass, title, expanded, onToggle, children, headerExtra }) => (
  <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
    <button type="button" onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <Icon size={20} className={iconClass} />
        <h3 className="text-lg font-semibold text-white truncate">{title}</h3>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {headerExtra}
        {expanded ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
      </div>
    </button>
    <AnimatePresence>
      {expanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
          <div className="p-4 pt-0">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const TVCPManager = ({ isOpen, onClose, fiscalYearId }) => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.declarationsFiscalesPage.${key}`, opts);
  const [loading, setLoading] = useState(false);
  const [tvcpData, setTvcpData] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [tvcpManagerTab, setTvcpManagerTab] = useState('overview');
  const tvcpManagerTabs = useMemo(() => ([
    { id: 'overview', label: p('overview'), icon: BarChart3 },
    { id: 'actions', label: p('actions_section'), icon: Calculator },
    { id: 'grid', label: p('tvcp_grid'), icon: LayoutGrid },
    { id: 'note', label: p('nscf_note'), icon: AlertTriangle },
  ]), [p]);

  const components = useMemo(
    () => TVCP_COMPONENT_DEFS.map((c) => ({
      code: c.code,
      label: p(c.labelKey),
      shortLabel: p(c.shortLabelKey) })),
    [t, i18n.language]
  );

  const rows = useMemo(
    () => TVCP_ROW_DEFS.map(({ labelKey, ...row }) => ({ ...row, label: p(labelKey) })),
    [t, i18n.language]
  );

  useEffect(() => {
    if (isOpen && fiscalYearId) {
      loadTVCP();
    }
  }, [isOpen, fiscalYearId]);

  const loadTVCP = async () => {
    setLoading(true);
    try {
      const data = await getTVCP(fiscalYearId);
      
      if (data.length === 0) {
        // Generate TVCP structure if doesn't exist
        await generateTVCPStructure();
      } else {
        setTvcpData(data);
      }
    } catch (error) {
      logger.error('Load TVCP error:', error);
      alert(p('alert_load_error'));
    } finally {
      setLoading(false);
    }
  };

  const generateTVCPStructure = async () => {
    try {
      const data = await generateTVCP(fiscalYearId);
      setTvcpData(data);
    } catch (error) {
      logger.error('Generate TVCP error:', error);
    }
  };

  const getValue = (componentCode, rowKey) => {
    const component = tvcpData.find(c => c.component === componentCode);
    if (!component) return 0;
    return parseFloat(component[rowKey] || 0);
  };

  const handleCellChange = (componentCode, rowKey, value) => {
    const numValue = parseFloat(value) || 0;
    
    setTvcpData(prev => 
      prev.map(comp => 
        comp.component === componentCode
          ? { ...comp, [rowKey]: numValue }
          : comp
      )
    );
    
    setHasChanges(true);
  };

  const calculateTotal = (rowKey) => {
    return components.reduce((sum, comp) => sum + getValue(comp.code, rowKey), 0);
  };

  const handleSave = async () => {
    if (!hasChanges) {
      alert(p('alert_no_changes'));
      return;
    }

    setLoading(true);
    try {
      // Update each component
      for (const comp of tvcpData) {
        await updateTVCPComponent(comp.id, {
          solde_n2: comp.solde_n2,
          changement_methode_n1: comp.changement_methode_n1,
          correction_erreurs_n1: comp.correction_erreurs_n1,
          reevaluation_immo_n1: comp.reevaluation_immo_n1,
          augmentation_capital_n1: comp.augmentation_capital_n1,
          dividendes_payes_n1: comp.dividendes_payes_n1,
          affectation_resultat_n1: comp.affectation_resultat_n1,
          resultat_net_n1: comp.resultat_net_n1,
          changement_methode_n: comp.changement_methode_n,
          correction_erreurs_n: comp.correction_erreurs_n,
          reevaluation_immo_n: comp.reevaluation_immo_n,
          augmentation_capital_n: comp.augmentation_capital_n,
          dividendes_payes_n: comp.dividendes_payes_n,
          affectation_resultat_n: comp.affectation_resultat_n,
          resultat_net_n: comp.resultat_net_n,
          user_code: '01'
        });
      }

      // Recalculate
      await handleRecalculate();
      
      setHasChanges(false);
      alert(p('alert_tvcp_saved'));
    } catch (error) {
      logger.error('Save TVCP error:', error);
      alert(p('alert_save_error_msg', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    setLoading(true);
    try {
      const data = await recalculateTVCP(fiscalYearId);
      setTvcpData(data);
      setHasChanges(false);
    } catch (error) {
      logger.error('Recalculate error:', error);
      alert(p('alert_recalc_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setLoading(true);
    try {
      const data = await exportTVCP(fiscalYearId, format);
      if (data) {
        // Generate Excel on frontend
        if (format === 'excel') {
          generateExcelExport(data);
        }
      }
    } catch (error) {
      logger.error('Export error:', error);
      alert(p('alert_export_error'));
    } finally {
      setLoading(false);
    }
  };

  const generateExcelExport = (data) => {
    // Simple CSV export (can be enhanced with xlsx library)
    let csv = `${p('csv_header')}\n\n`;
    csv += `${p('col_label')},` + components.map(c => c.shortLabel).join(',') + `,${p('total')}\n`;
    
    rows.forEach(row => {
      const rowData = [row.label];
      let rowTotal = 0;
      
      components.forEach(comp => {
        const val = getValue(comp.code, row.key);
        rowData.push(val.toFixed(2));
        rowTotal += val;
      });
      
      rowData.push(rowTotal.toFixed(2));
      csv += rowData.join(',') + '\n';
    });

    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `TVCP_${new Date().getFullYear()}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (isOpen) {
      setTvcpManagerTab('overview');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const closingTotal = calculateTotal('solde_n');

  return (
    <FiscalModalShell
      open={isOpen}
      onClose={onClose}
      title={p('tvcp_title')}
      Icon={TrendingUp}
      iconClass="text-blue-400"
      maxWidth="1400px"
      footer={(
        <>
          {hasChanges && <span className="text-yellow-300 text-sm mr-auto self-center">{p('unsaved_changes')}</span>}
          <button type="button" onClick={onClose} className="px-5 py-2 text-white/70 hover:text-white transition-colors">{p('close')}</button>
          <button type="button" onClick={handleSave} disabled={!hasChanges || loading} className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg shadow-blue-500/30 text-white rounded-xl font-medium hover:opacity-90 transition-all flex items-center gap-2 disabled:opacity-50">
            <Save size={16} />
            {p('save')}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <div className="shrink-0 border-b border-white/10 pb-0">
          <div className="flex flex-wrap gap-1" role="tablist">
            {tvcpManagerTabs.map((tab) => {
              const TabIcon = tab.icon;
              const active = tvcpManagerTab === tab.id;
              return (
                <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setTvcpManagerTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px' : 'text-white/55 hover:text-white hover:bg-white/5'}`}>
                  <TabIcon size={16} />
                  <span>{tab.label}</span>
                  {tab.id === 'grid' && loading ? <RefreshCw size={14} className="animate-spin text-white/50" /> : null}
                </button>
              );
            })}
          </div>
        </div>

        {tvcpManagerTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3">
              <p className="text-white/80 text-sm">
                {p('tvcp_overview_desc')}
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10 text-center">
              <p className="text-white/60 text-xs">{p('components')}</p>
              <p className="text-lg font-bold text-blue-300">7</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10 text-center">
              <p className="text-white/60 text-xs">{p('rows')}</p>
              <p className="text-lg font-bold text-white">{rows.length}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10 text-center">
              <p className="text-white/60 text-xs">{p('closing_balance_n')}</p>
              <p className="text-lg font-bold text-green-400">{formatBrandMoney(closingTotal)}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10 text-center md:col-start-2">
              <p className="text-white/60 text-xs">{p('status_label')}</p>
              <p className={`text-sm font-bold ${hasChanges ? 'text-yellow-400' : 'text-green-400'}`}>{hasChanges ? p('modified') : p('up_to_date')}</p>
            </div>
          </div>
        )}

        {tvcpManagerTab === 'actions' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3 flex flex-wrap gap-2">
              <button type="button" onClick={handleSave} disabled={!hasChanges || loading} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg shadow-blue-500/30 text-white rounded-xl font-medium hover:opacity-90 transition-all disabled:opacity-50">
                <Save size={16} />
                {p('save')}
              </button>
              <button type="button" onClick={handleRecalculate} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/15 transition-all disabled:opacity-50">
                <Calculator size={16} />
                {p('recalculate')}
              </button>
              <button type="button" onClick={() => handleExport('excel')} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/30 text-green-200 rounded-xl font-medium hover:bg-green-500/30 transition-all disabled:opacity-50">
                <Download size={16} />
                {p('export_excel')}
              </button>
            </div>
          </div>
        )}

        {tvcpManagerTab === 'grid' && (
          loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="h-8 w-8 text-white/40 animate-spin" />
            </div>
          ) : (
            <div className="glass-table-scroll overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600/30 to-blue-700/30 border-b-2 border-white/20">
                    <th className="text-left p-3 text-white font-bold text-sm min-w-[280px] sticky left-0 bg-slate-800/95">{p('equity_components')}</th>
                    {components.map((comp) => (
                      <th key={comp.code} className="text-center p-3 text-white font-semibold text-xs min-w-[120px]">
                        <div className="flex flex-col">
                          <span>{comp.shortLabel}</span>
                          <span className="text-white/50 font-normal mt-1">{comp.code}</span>
                        </div>
                      </th>
                    ))}
                    <th className="text-center p-3 text-yellow-300 font-bold text-sm min-w-[120px]">{p('total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const rowTotal = calculateTotal(row.key);
                    return (
                      <tr key={row.key} className={`border-b border-white/10 hover:bg-white/5 transition-colors ${row.type === 'opening' || row.type === 'calculated' ? 'bg-white/5' : ''}`}>
                        <td className={`p-3 text-white text-sm sticky left-0 bg-slate-800/95 ${row.bold ? 'font-bold' : ''} ${row.color === 'blue' ? 'text-blue-300' : row.color === 'green' ? 'text-green-300' : ''}`}>
                          {row.label}
                          {row.type === 'calculated' && <span className="ml-2 text-xs text-white/40">{p('auto')}</span>}
                        </td>
                        {components.map((comp) => {
                          const value = getValue(comp.code, row.key);
                          return (
                            <td key={comp.code} className="p-2 text-center">
                              {row.editable ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={value}
                                  onChange={(e) => handleCellChange(comp.code, row.key, e.target.value)}
                                  className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-center focus:ring-2 focus:ring-blue-500"
                                />
                              ) : (
                                <div className={`px-2 py-1.5 rounded font-semibold ${row.color === 'blue' ? 'bg-blue-500/20 text-blue-300' : row.color === 'green' ? 'bg-green-500/20 text-green-300' : 'text-white/60'}`}>
                                  {formatBrandMoney(value)}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className={`p-3 text-center font-bold ${row.color === 'blue' ? 'text-blue-300' : row.color === 'green' ? 'text-green-300' : 'text-yellow-300'}`}>
                          {formatBrandMoney(rowTotal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}

        {tvcpManagerTab === 'note' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3">
              <p className="text-white/80 text-sm">
                {p('nscf_note_text')}
              </p>
              <p className="text-white/60 text-xs mt-2">
                {p('nscf_formula')}
              </p>
            </div>
          </div>
        )}
      </div>
    </FiscalModalShell>
  );
};

const DECLARATIONS_LIST_COLUMNS = [
  { key: 'code', labelKey: 'col_declaration_no' },
  { key: 'name', labelKey: 'col_label' },
  { key: 'period', labelKey: 'col_period' },
  { key: 'dueDate', labelKey: 'col_due_date' },
  { key: 'amount', labelKey: 'col_amount' },
  { key: 'status', labelKey: 'col_status' },
];

const DeclarationsFiscalesPage = () => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  useComptabiliteConfig();
  const p = (key, opts) => t(`modules.comptabilite.pages.declarationsFiscalesPage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const [fiscalYearId, setFiscalYearId] = useState('');
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');

  const [taxDeclarationsData, setTaxDeclarationsData] = useState({
    vat: buildDefaultVatReturn(settings),
    zakat: buildDefaultZakatReturn(),
    cit: buildDefaultCitReturn(),
    wht: buildDefaultWhtReturn() });
  const [taxCalendar, setTaxCalendar] = useState([]);
  const [selectedTaxDeclaration, setSelectedTaxDeclaration] = useState('vat');
  const [showAdvancedOptionsTax, setShowAdvancedOptionsTax] = useState(false);
  const [showTVCPManager, setShowTVCPManager] = useState(false);
  const [showCitForm, setShowCitForm] = useState(false);
  const [showZakatForm, setShowZakatForm] = useState(false);
  const [showWhtForm, setShowWhtForm] = useState(false);
  const [showTVATeledeClarer, setShowTVATeledeClarer] = useState(false);
  const [showFormulaireVierge, setShowFormulaireVierge] = useState(false);
  const [showNouvelleDeclaration, setShowNouvelleDeclaration] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('general');
  const [calendarQuickFilter, setCalendarQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [recordStatusOverrides, setRecordStatusOverrides] = useState({});
  const [storedDeclarations, setStoredDeclarations] = useState([]);
  const [viewingDeclaration, setViewingDeclaration] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  const columnFilterRules = useMemo(() => [
    textColumnRule('code', (row) => row.code ?? ''),
    textColumnRule('name', (row) => row.name ?? ''),
    textColumnRule('period', (row) => row.period ?? ''),
    textColumnRule('dueDate', (row) => row.dueDate ?? ''),
    textColumnRule('amount', (row) => String(row.amount ?? '')),
    textColumnRule('status', (row) => row.status ?? ''),
  ], []);
  const [tvaFormData, setTvaFormData] = useState(EMPTY_TVA_FORM);
  const [nouvelleFormData, setNouvelleFormData] = useState(EMPTY_NOUVELLE_DECL_FORM);
  const [formulaireFormData, setFormulaireFormData] = useState(EMPTY_FORMULAIRE_FORM);
  const [tvaExpandedSections, setTvaExpandedSections] = useState(DEFAULT_TVA_SECTIONS);
  const [nouvelleExpandedSections, setNouvelleExpandedSections] = useState(DEFAULT_NOUVELLE_SECTIONS);
  const [formulaireExpandedSections, setFormulaireExpandedSections] = useState(DEFAULT_FORMULAIRE_SECTIONS);

  const toggleTvaSection = (section) => setTvaExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  const toggleNouvelleSection = (section) => setNouvelleExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  const toggleFormulaireSection = (section) => setFormulaireExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

  const declarationStatusOptions = useMemo(
    () => DECLARATION_STATUS_DEFS.map(({ value, labelKey }) => ({ value, label: p(labelKey) })),
    [t, i18n.language]
  );

  const recordStatusOptions = useMemo(
    () => mapComptabiliteStatusOptions(DECL_RECORD_STATUS_DEFS, p),
    [t, i18n.language]
  );

  const handleDeclarationStatusChange = async (recordId, newStatus) => {
    setRecordStatusOverrides((prev) => ({ ...prev, [recordId]: newStatus }));
    try {
      const row = storedDeclarations.find((r) => r.id === recordId);
      if (row) {
        await updateTaxDeclaration(recordId, { ...row, status: newStatus });
        setRefreshKey((k) => k + 1);
        notifyUpdated(showNotify, p('col_declaration_no', { defaultValue: 'الإقرار' }));
      }
    } catch (error) {
      logger.error('Tax declaration status update error:', error);
      notifyError(showNotify, p('alert_submit_error', { defaultValue: 'خطأ أثناء التحديث' }));
    }
  };

  const periodSelectOptions = useMemo(
    () => DECL_PERIOD_SELECT_DEFS.map(({ value, labelKey, isDefault }) => ({ value, label: p(labelKey), isDefault })),
    [t, i18n.language]
  );

  const formatSelectOptions = useMemo(
    () => DECL_FORMAT_SELECT_DEFS.map(({ value, labelKey, isDefault }) => ({ value, label: p(labelKey), isDefault })),
    [t, i18n.language]
  );

  const yesNoOptions = useMemo(
    () => [
      { value: 'yes', label: p('option_yes'), isDefault: true },
      { value: 'no', label: p('option_no'), isDefault: true },
    ],
    [t, i18n.language]
  );

  const initialStatusOptions = useMemo(
    () => [
      { value: 'draft', label: p('record_draft'), isDefault: true },
      { value: 'pending', label: p('record_pending'), isDefault: true },
    ],
    [t, i18n.language]
  );

  const languageOptions = useMemo(
    () => [
      { value: 'fr', label: p('lang_french'), isDefault: true },
      { value: 'ar', label: p('lang_arabic'), isDefault: true },
    ],
    [t, i18n.language]
  );

  const vatFilingPeriodOptions = useMemo(
    () => [
      { value: 'monthly', label: p('period_monthly'), isDefault: true },
      { value: 'quarterly', label: p('quarterly'), isDefault: true },
    ],
    [t, i18n.language]
  );

  const resetTvaModal = () => {
    setTvaFormData(EMPTY_TVA_FORM);
    setTvaExpandedSections(DEFAULT_TVA_SECTIONS);
    setShowTVATeledeClarer(false);
  };

  const resetNouvelleModal = () => {
    setNouvelleFormData(EMPTY_NOUVELLE_DECL_FORM);
    setNouvelleExpandedSections(DEFAULT_NOUVELLE_SECTIONS);
    setShowNouvelleDeclaration(false);
  };

  const openNouvelleDeclarationModal = () => {
    setNouvelleFormData({
      ...EMPTY_NOUVELLE_DECL_FORM,
      declarationCode: generateDeclarationCode(),
      fiscalYear: fiscalYearLabel || fiscalYearId,
    });
    setNouvelleExpandedSections(DEFAULT_NOUVELLE_SECTIONS);
    setShowNouvelleDeclaration(true);
  };

  const resetFormulaireModal = () => {
    setFormulaireFormData(EMPTY_FORMULAIRE_FORM);
    setFormulaireExpandedSections(DEFAULT_FORMULAIRE_SECTIONS);
    setShowFormulaireVierge(false);
  };

  const openTvaModal = () => {
    setTvaFormData(EMPTY_TVA_FORM);
    setTvaExpandedSections(DEFAULT_TVA_SECTIONS);
    setShowTVATeledeClarer(true);
  };

  const handleSaveVatDeclaration = async (e) => {
    e.preventDefault();
    if (!tvaFormData.period) {
      window.alert(p('choose_period'));
      return;
    }

    const collected = Number.parseFloat(tvaFormData.tvaCollectee) || 0;
    const deductible = Number.parseFloat(tvaFormData.tvaDeductible) || 0;
    const net = Math.max(0, collected - deductible);

    setTaxDeclarationsData((prev) => {
      const vat = prev.vat || buildDefaultVatReturn(settings);
      const sections = (vat.sections || []).map((section) => {
        if (section.id !== 'recap') return section;
        return {
          ...section,
          totals: {
            ...section.totals,
            tvaCollectee: collected,
            tvaDeductible: deductible,
            tvaNetteAVerser: net,
            netAVerser: net } };
      });

      return {
        ...prev,
        vat: {
          ...vat,
          sections,
          status: p('submitted'),
          manualFiling: {
            period: tvaFormData.period,
            declarationType: tvaFormData.declarationType,
            notes: tvaFormData.notes,
            savedAt: new Date().toISOString() } } };
    });

    try {
      await createTaxDeclaration({
        declaration_number: generateDeclarationCode(),
        type: 'vat',
        declarationType: tvaFormData.declarationType,
        title: vatData.title,
        titleAr: vatData.titleAr || vatData.title,
        period: tvaFormData.period,
        fiscalYear: fiscalYearId,
        status: 'submitted',
        totalTax: net,
        netAmount: net,
        notes: tvaFormData.notes });
    } catch {
      // Local save only when API is unavailable
    }

    window.alert(p('alert_submitted'));
    setRefreshKey((k) => k + 1);
    resetTvaModal();
  };

  const renderColumnFilter = (key, label, center = false) => (
    <TableColumnHeaderFilter
      columnKey={key}
      label={label}
      value={columnFilters[key] || ''}
      onChange={(v) => setColumnFilter(key, v)}
      editingKey={editingKey}
      onEditingKeyChange={setEditingKey}
      align={center ? 'center' : 'start'}
      thClassName="text-white"
    />
  );

  const getTypeBadge = (type) => {
    const map = {
      vat: 'bg-blue-500/20 text-blue-300',
      cit: 'bg-orange-500/20 text-orange-300',
      zakat: 'bg-purple-500/20 text-purple-300',
      wht: 'bg-green-500/20 text-green-300',
      tva: 'bg-blue-500/20 text-blue-300' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${map[type] || 'bg-gray-500/20 text-gray-300'}`}>
        {(type || '').toUpperCase()}
      </span>
    );
  };

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);

  useEffect(() => {
    getCurrentFiscalYear()
      .then((fy) => {
        if (fy?.id) setFiscalYearId(fy.id);
        if (fy?.year_name) setFiscalYearLabel(String(fy.year_name));
        else if (fy?.start_date) setFiscalYearLabel(String(fy.start_date).slice(0, 4));
      })
      .catch(() => {
        const year = new Date().getFullYear();
        setFiscalYearId(`fy_${year}`);
        setFiscalYearLabel(String(year));
      });
  }, []);

  useEffect(() => {
    getTaxDeclarations().catch(() => ({})).then((taxDecl) => {
      if (taxDecl && typeof taxDecl === 'object') {
        setTaxDeclarationsData(prev => ({ ...prev, ...taxDecl }));
      }
    });
    listTaxDeclarationRecords()
      .then((rows) => setStoredDeclarations(Array.isArray(rows) ? rows : []))
      .catch(() => setStoredDeclarations([]));
    getTaxCalendar()
      .then((rows) => setTaxCalendar(Array.isArray(rows) ? rows.map(normalizeTaxCalendarItem) : []))
      .catch(() => setTaxCalendar([]));
  }, [refreshKey]);

  const reloadDeclarations = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCalendar = useMemo(() => {
    return taxCalendar.filter((item) => {
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (calendarQuickFilter !== 'all' && item.status !== calendarQuickFilter) return false;
      if (activeDistributionFilter) {
        const typeMap = { 'ضريبة القيمة المضافة': 'vat', 'الزكاة': 'zakat', 'ضريبة الدخل': 'cit', 'ضريبة الاستقطاع': 'wht' };
        if (typeMap[activeDistributionFilter] && item.type !== typeMap[activeDistributionFilter]) return false;
      }
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!item.declaration?.toLowerCase().includes(term) && !item.date?.toLowerCase().includes(term) && !item.type?.toLowerCase().includes(term)) {
          return false;
        }
      }
      return true;
    });
  }, [taxCalendar, statusFilter, calendarQuickFilter, activeDistributionFilter, searchTerm]);

  const vatData = taxDeclarationsData.vat || buildDefaultVatReturn(settings);
  const whtData = taxDeclarationsData.wht || buildDefaultWhtReturn();
  const zakatData = taxDeclarationsData.zakat || buildDefaultZakatReturn();
  const citData = taxDeclarationsData.cit || buildDefaultCitReturn();

  const generateDeclarationCode = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'taxDeclaration', 'DEC');
    return generateEntityReference(prefix, storedDeclarations, (row) => row.declaration_number || row.code);
  }, [settings, storedDeclarations]);

  const declarationRecords = useMemo(() => storedDeclarations.map((row) => ({
    id: row.id,
    code: row.declaration_number || row.code || '',
    name: row.title || row.declaration_title || row.tax_type || '',
    period: row.period || '',
    dueDate: row.due_date || '',
    amount: row.amount ?? row.net_amount ?? 0,
    status: recordStatusOverrides[row.id] || row.status || 'draft',
    type: row.tax_type || row.declaration_type || '',
  })), [storedDeclarations, recordStatusOverrides]);

  const declarationStats = useMemo(() => ({
    total: declarationRecords.length,
    pending: declarationRecords.filter((r) => r.status === 'pending' || r.status === 'draft').length,
    submitted: declarationRecords.filter((r) => r.status === 'submitted').length,
    totalAmount: declarationRecords.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0),
  }), [declarationRecords]);

  const openNewDeclarationForTab = (type) => {
    switch (type) {
      case 'vat':
        openTvaModal();
        break;
      case 'wht':
        setShowWhtForm(true);
        break;
      case 'zakat':
        setShowZakatForm(true);
        break;
      case 'cit':
        setShowCitForm(true);
        break;
      default:
        break;
    }
  };

  const filteredDeclarations = useMemo(() => {
    let list = declarationRecords;
    if (selectedTaxDeclaration) {
      list = list.filter((r) => !r.type || r.type === selectedTaxDeclaration);
    }
    if (statusFilter !== 'all') {
      list = list.filter((r) => r.status === statusFilter);
    }
    if (calendarQuickFilter !== 'all') {
      list = list.filter((r) => r.status === calendarQuickFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (r) => r.code?.toLowerCase().includes(term)
          || r.name?.toLowerCase().includes(term)
          || r.period?.toLowerCase().includes(term),
      );
    }
    return applyColumnFilters(list, columnFilters, columnFilterRules);
  }, [declarationRecords, selectedTaxDeclaration, statusFilter, calendarQuickFilter, searchTerm, columnFilters, columnFilterRules]);

  const footerTotals = useMemo(() => ({
    count: filteredDeclarations.length,
    amount: filteredDeclarations.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0),
  }), [filteredDeclarations]);

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount,
  } = useComptabiliteTableColumnVisibility(DECLARATIONS_LIST_COLUMNS, p);
  const declarationsTableColSpan = visibleDataColumnCount + 2;

  const {
    selectedRecords: selectedDeclarations,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredDeclarations, (row) => row.id);

  const handleViewDeclaration = (record) => {
    setViewingDeclaration(record);
    setShowViewModal(true);
  };

  const handleEditDeclaration = (record) => {
    const type = record.type || selectedTaxDeclaration;
    if (type) setSelectedTaxDeclaration(type);
    openNewDeclarationForTab(type || 'vat');
  };

  const handleDeleteDeclaration = async (id) => {
    if (!window.confirm(p('confirm_bulk_delete', { count: 1, defaultValue: 'حذف هذا الإقرار؟' }))) return;
    try {
      await deleteTaxDeclaration(id);
      notifyDeleted(showNotify, p('col_declaration_no', { defaultValue: 'الإقرار' }));
      clearSelection();
      reloadDeclarations();
    } catch (error) {
      logger.error('Tax declaration delete error:', error);
      notifyError(showNotify, p('alert_submit_error', { defaultValue: 'خطأ أثناء الحذف' }));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedDeclarations.length) return;
    if (window.confirm(p('confirm_bulk_delete', { count: selectedDeclarations.length }))) {
      try {
        const deleteCount = selectedDeclarations.length;
        await Promise.all(selectedDeclarations.map((id) => deleteTaxDeclaration(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('col_declaration_no', { defaultValue: 'إقرار' }));
        clearSelection();
        reloadDeclarations();
      } catch (error) {
        logger.error('Bulk delete tax declarations error:', error);
        notifyError(showNotify, p('alert_submit_error', { defaultValue: 'خطأ أثناء الحذف' }));
      }
    }
  };

  const handleNouvelleDeclarationSubmit = async (e) => {
    e.preventDefault();
    if (!nouvelleFormData.declarationType || !nouvelleFormData.period) {
      window.alert(p('choose_period'));
      return;
    }
    try {
      await createTaxDeclaration({
        declaration_number: nouvelleFormData.declarationCode || generateDeclarationCode(),
        type: nouvelleFormData.declarationType,
        period: nouvelleFormData.period,
        fiscalYear: nouvelleFormData.fiscalYear || fiscalYearId,
        status: nouvelleFormData.status || 'draft',
        notes: nouvelleFormData.notes,
      });
      setSelectedTaxDeclaration(nouvelleFormData.declarationType);
      openNewDeclarationForTab(nouvelleFormData.declarationType);
      reloadDeclarations();
    } catch {
      setSelectedTaxDeclaration(nouvelleFormData.declarationType);
      openNewDeclarationForTab(nouvelleFormData.declarationType);
    }
    resetNouvelleModal();
  };

  return (
    <div className="space-y-6 employees-container">
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ComptabiliteBulkDeleteFilterAction
              count={selectedDeclarations.length}
              onClick={handleBulkDelete}
              label={p('delete_count', { count: selectedDeclarations.length, defaultValue: `حذف (${selectedDeclarations.length})` })}
              title={p('delete_count', { count: selectedDeclarations.length, defaultValue: `حذف (${selectedDeclarations.length})` })}
            />
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
              tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
            />
          </div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FileText className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button type="button" onClick={reloadDeclarations} className="refresh-button" title={p('refresh_data')}>
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3 shrink-0 flex-wrap">
          <button
            type="button"
            onClick={() => setShowAdvancedOptionsTax(!showAdvancedOptionsTax)}
            className={`${showAdvancedOptionsTax ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold flex items-center gap-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
          </button>
          <button type="button" onClick={() => setShowTVCPManager(true)} className="bg-white/5 border border-white/20 hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            <span className="hidden sm:inline">{p('tvcp_title')}</span>
          </button>
          <button type="button" onClick={openNouvelleDeclarationModal} className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2">
            <Plus className="h-5 w-5" />
            <span>{p('new_fiscal_declaration')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { labelKey: 'stat_total', value: declarationStats.total, icon: FileText, color: 'from-blue-500 to-blue-600' },
          { labelKey: 'stat_pending', value: declarationStats.pending, icon: Clock, color: 'from-orange-500 to-orange-600' },
          { labelKey: 'stat_validated', value: declarationStats.submitted, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { labelKey: 'stat_tva_to_pay', value: formatBrandMoney(declarationStats.totalAmount), icon: DollarSign, color: 'from-purple-500 to-purple-600' },
        ].map((stat, index) => (
          <motion.div key={stat.labelKey} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }} className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm">{p(stat.labelKey)}</p>
                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showAdvancedOptionsTax && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-4 overflow-hidden">
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'general', labelKey: 'view_general' },
                { id: 'by-type', labelKey: 'view_by_type' },
                { id: 'by-status', labelKey: 'view_by_status' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setMainViewMode(tab.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium ${mainViewMode === tab.id ? 'bg-blue-500/30 text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
                >
                  {p(tab.labelKey)}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {declarationStatusOptions.filter((opt) => opt.value !== 'all').map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setCalendarQuickFilter(calendarQuickFilter === opt.value ? 'all' : opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm ${calendarQuickFilter === opt.value ? 'bg-purple-500/30 text-purple-200 border border-purple-400/50' : 'bg-white/5 text-white/60'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <>
              {/* Distribution Overview */}
              {(mainViewMode === 'general' || mainViewMode === 'by-type') && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.1 }} 
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">{p('overview_title')}</h3>
                  {activeDistributionFilter && (
                    <button
                      type="button"
                      onClick={() => setActiveDistributionFilter(null)}
                      className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                    >
                      <XCircle size={14} />{p('reset_filter')}
                    </button>
                  )}
                </div>
                <div {...getErpOverviewGridAttrs(4, '4')}>
                  {[
                    { name: "ضريبة القيمة المضافة", tabId: 'vat', count: declarationRecords.filter(t => t.type === 'vat').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
                    { name: "ضريبة الاستقطاع", tabId: 'wht', count: declarationRecords.filter(t => t.type === 'wht').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
                    { name: "الزكاة", tabId: 'zakat', count: declarationRecords.filter(t => t.type === 'zakat').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
                    { name: "ضريبة الدخل", tabId: 'cit', count: declarationRecords.filter(t => t.type === 'cit').length, color: "bg-gradient-to-r from-orange-500 to-orange-600" }
                  ].map((item) => (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => {
                        setActiveDistributionFilter(activeDistributionFilter === item.name ? null : item.name);
                        setSelectedTaxDeclaration(item.tabId);
                      }}
                      className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left w-full ${activeDistributionFilter === item.name ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h4 className={`font-medium ${activeDistributionFilter === item.name ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                        <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white font-semibold`}>{item.count}</div>
                      </div>
                      <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                        <div 
                          className={`h-3 rounded-full ${item.color} transition-all`} 
                          style={{ width: `${declarationRecords.length > 0 ? (item.count / declarationRecords.length * 100).toFixed(1) : 0}%` }}
                        ></div>
                      </div>
                      <p className={`mt-2 text-sm ${activeDistributionFilter === item.name ? 'text-white/80' : 'text-white/60'}`}>
                        {p('percent_of_total', { percent: declarationRecords.length > 0 ? (item.count / declarationRecords.length * 100).toFixed(1) : 0 })}
                      </p>
                    </button>
                  ))}
                </div>
              </motion.div>
              )}

          {/* Top Controls Bar - Facturiers Pattern */}
          <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
              <div className="overflow-x-auto rounded-b-3xl">
                <table className="w-full employees-table data-table">
                  <thead className="glass-table-head">
                    <tr>
                      <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                      {isColVisible('code') ? renderColumnFilter('code', p('col_declaration_no')) : null}
                      {isColVisible('name') ? renderColumnFilter('name', p('col_label')) : null}
                      {isColVisible('period') ? renderColumnFilter('period', p('col_period')) : null}
                      {isColVisible('dueDate') ? renderColumnFilter('dueDate', p('col_due_date')) : null}
                      {isColVisible('amount') ? renderColumnFilter('amount', p('col_amount')) : null}
                      {isColVisible('status') ? renderColumnFilter('status', p('col_status'), true) : null}
                      <QawaemTableHeaderActionsCell
                        label={p('col_actions')}
                        columns={qawaemColumnDefs}
                        onToggle={handleToggleColumn}
                        visibleCount={qawaemVisibleColumnCount}
                        totalCount={qawaemColumnDefs.length}
                      thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10 employees-table-body">
                    {filteredDeclarations.length > 0 ? filteredDeclarations.map((record, index) => (
                      <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedDeclarations.includes(record.id) ? 'bg-white/10' : ''}`}>
                        <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedDeclarations.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>
                        {isColVisible('code') ? (
                        <td className="px-6 py-4 text-white font-semibold">{record.code}</td>
                        ) : null}
                        {isColVisible('name') ? (
                        <td className="px-6 py-4 text-white font-semibold">{record.name}</td>
                        ) : null}
                        {isColVisible('period') ? (
                        <td className="px-6 py-4 text-white/80">{record.period || p('em_dash')}</td>
                        ) : null}
                        {isColVisible('dueDate') ? (
                        <td className="px-6 py-4 text-white/80">{record.dueDate || p('em_dash')}</td>
                        ) : null}
                        {isColVisible('amount') ? (
                        <td className="px-6 py-4 text-white font-semibold">{formatBrandMoney(record.amount)}</td>
                        ) : null}
                        {isColVisible('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.status}
                              recordId={record.id}
                              options={recordStatusOptions}
                              onChange={handleDeclarationStatusChange}
                              title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                            />
                          </div>
                        </td>
                        ) : null}
                        <td className="px-6 py-4">
                          <QawaemErpRowActions
                            onView={() => handleViewDeclaration(record)}
                            onEdit={() => handleEditDeclaration(record)}
                            onDelete={() => handleDeleteDeclaration(record.id)}
                            viewTitle={p('action_view')}
                            editTitle={p('action_edit')}
                            deleteTitle={t('common.delete', { defaultValue: 'حذف' })}
                          />
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={declarationsTableColSpan} className="text-center py-12 text-white/60">
                          <FileText size={48} className="mx-auto mb-4 text-white/30" />
                          <p className="text-lg">{p('empty_declarations')}</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="bg-white/5 border-t border-white/10">
                      <td colSpan={declarationsTableColSpan} className="px-6 py-4">
                        <div className="flex items-center justify-between text-white/70 text-sm">
                          <span>{p('footer_total', { count: footerTotals.count })}</span>
                          <span>{p('footer_selected')} <span className="text-white font-semibold">{selectedDeclarations.length}</span></span>
                          <span>{p('footer_amount')} <span className="text-white font-semibold">{formatBrandMoney(footerTotals.amount)}</span></span>
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {viewMode === 'cards' && (
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
                {filteredDeclarations.length > 0 ? filteredDeclarations.map((record, index) => (
                  <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-4 mb-4 flex-wrap gap-2">
                          <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <Receipt size={20} className="text-emerald-400" />
                            {record.code}
                          </h3>
                          {getTypeBadge(record.type)}
                          <QawaemClickableStatusBadge
                            status={record.status}
                            recordId={record.id}
                            options={recordStatusOptions}
                            onChange={handleDeclarationStatusChange}
                            title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                          {record.dueDate && <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">{record.dueDate}</span>}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('card_identification')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              <div><span className="font-medium">{p('card_code')}</span> {record.code}</div>
                              <div><span className="font-medium">{p('card_label')}</span> {record.name}</div>
                              <div><span className="font-medium">{p('card_type')}</span> {record.type?.toUpperCase()}</div>
                            </div>
                          </div>
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('card_period_due')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              <div className="flex items-center gap-2"><Calendar size={16} className="text-blue-400" /><span className="font-medium">{p('card_period')}</span> {record.period || p('na')}</div>
                              <div className="flex items-center gap-2"><Clock size={16} className="text-orange-400" /><span className="font-medium">{p('card_due')}</span> {record.dueDate || p('na')}</div>
                            </div>
                          </div>
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('card_amounts')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              <div className="p-2 bg-green-500/10 rounded border border-green-500/30">
                                <span className="font-medium text-green-300 text-xs">{p('card_declared_amount')}</span>
                                <p className="text-green-200 text-sm mt-1 font-semibold">{formatBrandMoney(record.amount)}</p>
                              </div>
                            </div>
                          </div>
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('card_status_actions')}</h5>
                            <div className="space-y-2 text-sm text-white/70">
                              <div>
                                <QawaemClickableStatusBadge
                                  status={record.status}
                                  recordId={record.id}
                                  options={recordStatusOptions}
                                  onChange={handleDeclarationStatusChange}
                                  title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                                />
                              </div>
                              <p className="text-white/40 text-xs italic">{p('card_actions_hint')}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleViewDeclaration(record)}
                        onEdit={() => handleEditDeclaration(record)}
                        onDelete={() => handleDeleteDeclaration(record.id)}
                        viewTitle={p('action_view')}
                        editTitle={p('action_edit')}
                        deleteTitle={t('common.delete', { defaultValue: 'حذف' })}
                      />
                    </div>
                  </motion.div>
                )) : (
                  <div className="col-span-full text-center py-12">
                    <FileText size={48} className="mx-auto mb-4 text-white/30" />
                    <p className="text-white/60 text-lg">{p('empty_declarations')}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tax Calendar - GPS Styled */}
            <div className="lg:col-span-1">
              <div className="glass-panel glass-table-shell overflow-visible">
                <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 p-6 border-b border-white/10">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-2xl bg-gradient-to-r from-red-500 to-orange-600 flex items-center justify-center shadow-lg">
                      <Calendar className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{p('calendar_title')}</h3>
                      <p className="text-white/50 text-xs">{p('calendar_subtitle')}</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 space-y-3">
                  {filteredCalendar.length === 0 ? (
                    <div className="text-center py-8 text-white/50 text-sm">
                      <Calendar size={32} className="mx-auto mb-3 text-white/30" />
                      <p>{taxCalendar.length === 0 ? p('calendar_empty') : p('calendar_no_results')}</p>
                    </div>
                  ) : filteredCalendar.map((item, index) => (
                    <motion.div 
                      key={`${item.type}-${item.date}-${index}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`p-4 rounded-xl border transition-all hover:shadow-lg cursor-pointer ${
                        item.status === 'urgent' 
                          ? 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20' 
                          : item.status === 'upcoming'
                          ? 'bg-orange-500/10 border-orange-500/30 hover:bg-orange-500/20'
                          : 'bg-white/5 border-white/10 hover:bg-white/10'
                      }`}
                      onClick={() => {
                        const tabMap = { vat: 'vat', zakat: 'zakat', cit: 'cit', wht: 'wht' };
                        if (tabMap[item.type]) setSelectedTaxDeclaration(tabMap[item.type]);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          const tabMap = { vat: 'vat', zakat: 'zakat', cit: 'cit', wht: 'wht' };
                          if (tabMap[item.type]) setSelectedTaxDeclaration(tabMap[item.type]);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {item.status === 'urgent' && <AlertTriangle size={18} className="text-red-400" />}
                          {item.status === 'upcoming' && <Clock size={18} className="text-orange-400" />}
                          {item.status === 'future' && <CheckCircle size={18} className="text-green-400" />}
                          <span className={`text-sm font-semibold ${
                            item.status === 'urgent' ? 'text-red-400' : 
                            item.status === 'upcoming' ? 'text-orange-400' : 'text-white/80'
                          }`}>
                            {item.date}
                          </span>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          item.type === 'tva' ? 'bg-blue-500/20 text-blue-300' :
                          item.type === 'wht' ? 'bg-green-500/20 text-green-300' :
                          item.type === 'zakat' ? 'bg-purple-500/20 text-purple-300' :
                          'bg-gray-500/20 text-gray-300'
                        }`}>
                          {(item.type || 'vat').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-white/90 text-sm font-medium">{item.declaration}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Tax Declarations */}
            <div className="lg:col-span-2">
              <div className="space-y-6">
                {/* VAT Declaration — manual filing */}
                {selectedTaxDeclaration === 'vat' && (
                  <div className="glass-panel glass-table-shell overflow-visible">
                    <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 p-6 border-b border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-white">{vatData.title}</h3>
                          <p className="text-white/60 text-sm mt-1">{vatData.description}</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center space-x-2 mb-2">
                            <AlertTriangle size={18} className="text-red-400" />
                            <span className="text-red-400 text-sm font-semibold">الاستحقاق: {vatData.dueDate || '—'}</span>
                          </div>
                          <span className="px-4 py-1.5 rounded-full text-xs font-semibold bg-red-500/20 text-red-300 border border-red-500/30">
                            {vatData.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 space-y-6">
                      {(vatData.sections || []).map((section) => (
                        <div key={section.id}>
                          <h4 className="text-white font-semibold mb-4 text-lg">{section.title}</h4>
                          
                          {section.id !== 'recap' && (
                            <div className="overflow-x-auto rounded-2xl border border-white/10">
                              <table className="w-full">
                                <thead className="glass-table-head">
                                  <tr className="border-b border-white/10">
                                    <th className="px-6 py-4 text-left text-white font-semibold text-sm">{p('col_code')}</th>
                                    <th className="px-6 py-4 text-left text-white font-semibold text-sm">{p('col_description')}</th>
                                    <th className="px-6 py-4 text-right text-white font-semibold text-sm">{p('col_base_ht')}</th>
                                    <th className="px-6 py-4 text-right text-white font-semibold text-sm">{p('col_rate')}</th>
                                    <th className="px-6 py-4 text-right text-white font-semibold text-sm">{p('col_tva')}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                  {(section.items || []).map((item, index) => (
                                    <tr key={index} className="hover:bg-white/5 transition-colors">
                                      <td className="px-6 py-4 text-cyan-400 font-mono font-semibold">{item.code}</td>
                                      <td className="px-6 py-4">
                                        <div>
                                          <p className="text-white text-sm font-medium">{item.description}</p>
                                          <p className="text-white/50 text-xs mt-1">{item.descriptionAr}</p>
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 text-right text-white font-medium">
                                        {formatBrandMoney(item.baseHT || 0)}
                                      </td>
                                      <td className="px-6 py-4 text-right text-white/70 font-medium">
                                        {item.taux}
                                      </td>
                                      <td className="px-6 py-4 text-right text-green-400 font-semibold">
                                        {formatBrandMoney(item.tva || 0)}
                                      </td>
                                    </tr>
                                  ))}
                                  <tr className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 font-semibold border-t-2 border-blue-400/30">
                                    <td colSpan="2" className="px-6 py-4 text-white font-bold">{p('total_section', { section: section.title })}</td>
                                    <td className="px-6 py-4 text-right text-white font-bold">
                                      {formatBrandMoney(section.totalBaseHT || 0)}
                                    </td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4 text-right text-emerald-400 font-bold text-lg">
                                      {formatBrandMoney(section.totalTVA || 0)}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}

                          {section.id === 'recap' && (
                            <div className="bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-xl p-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                  <div className="flex justify-between">
                                    <span className="text-white/80">{p('tva_collected')}</span>
                                    <span className="text-green-400 font-semibold">{formatBrandMoney(section.totals?.tvaCollectee || 0)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-white/80">{p('tva_deductible')}</span>
                                    <span className="text-orange-400 font-semibold">{formatBrandMoney(section.totals?.tvaDeductible || 0)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-white/80">{p('previous_credit')}</span>
                                    <span className="text-blue-400 font-semibold">{formatBrandMoney(section.totals?.creditReportePeriodePrecedente || 0)}</span>
                                  </div>
                                  <div className="flex justify-between border-t border-white/20 pt-2">
                                    <span className="text-white font-bold">{p('net_tva')}</span>
                                    <span className="text-emerald-400 font-bold text-lg">{formatBrandMoney(section.totals?.tvaNetteAVerser || 0)}</span>
                                  </div>
                                </div>
                                <div className="flex items-center justify-center">
                                  <button 
                                    onClick={openTvaModal}
                                    className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-medium hover:from-blue-600 hover:to-blue-800 transition-all flex items-center space-x-2"
                                  >
                                    <Save size={16} />
                                    <span>{p('teledeclare')}</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* WHT Declaration */}
                {selectedTaxDeclaration === 'wht' && (
                  <div className="glass-panel glass-table-shell overflow-visible">
                    <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 p-6 border-b border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-white">{whtData.title}</h3>
                          <p className="text-white/60 text-sm mt-1">{whtData.descriptionAr || whtData.description}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-orange-400 text-sm font-semibold">الاستحقاق: {whtData.dueDate || '—'}</span>
                          <span className="block mt-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-orange-500/20 text-orange-300 border border-orange-500/30">{whtData.status}</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                      {(whtData.sections?.[0]?.items || []).map((item, index) => (
                        <div key={index} className="flex justify-between items-center bg-white/5 rounded-xl p-4 border border-white/10">
                          <div>
                            <p className="text-white font-medium">{item.descriptionAr}</p>
                            <p className="text-white/50 text-sm">النسبة: {item.rate}</p>
                          </div>
                          <span className="text-green-400 font-mono font-semibold">{formatBrandMoney(item.wht || 0)}</span>
                        </div>
                      ))}
                      <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4 flex justify-between items-center">
                        <span className="text-white font-bold">إجمالي ضريبة الاستقطاع:</span>
                        <span className="text-emerald-400 font-bold text-lg">{formatBrandMoney(whtData.totalWht || 0)}</span>
                      </div>
                      <button type="button" onClick={() => setShowWhtForm(true)} className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 mx-auto">
                        <FileCheck size={16} /><span>{p('submit_wht')}</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Zakat Declaration */}
                {selectedTaxDeclaration === 'zakat' && (
                  <div className="glass-panel glass-table-shell overflow-visible">
                    <div className="bg-gradient-to-r from-purple-600/20 to-violet-600/20 p-6 border-b border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-white">{zakatData.title}</h3>
                          <p className="text-white/60 text-sm mt-1">{zakatData.descriptionAr || zakatData.description}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-orange-400 text-sm font-semibold">الاستحقاق: {zakatData.dueDate || '—'}</span>
                          <span className="block mt-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-300 border border-purple-500/30">{zakatData.status}</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-3 bg-white/5 rounded-xl p-4 border border-white/10">
                          <div className="flex justify-between"><span className="text-white/80">وعاء الزكاة:</span><span className="text-white font-medium">{formatBrandMoney(zakatData.zakatBase || 0)}</span></div>
                          <div className="flex justify-between"><span className="text-white/80">نسبة الزكاة:</span><span className="text-white font-medium">{zakatData.zakatRate || 2.5}%</span></div>
                          <div className="flex justify-between border-t border-white/20 pt-2"><span className="text-white font-bold">الزكاة المستحقة:</span><span className="text-emerald-400 font-bold text-lg">{formatBrandMoney(zakatData.zakatDue || 0)}</span></div>
                        </div>
                        <div className="flex items-center justify-center">
                          <button type="button" onClick={() => setShowZakatForm(true)} className="bg-gradient-to-r from-purple-600 to-violet-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 shadow-lg shadow-purple-500/30">
                            <Save size={16} /><span>{p('submit_zakat')}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* CIT Declaration */}
                {selectedTaxDeclaration === 'cit' && (
                  <div className="glass-panel glass-table-shell overflow-visible">
                    <div className="bg-gradient-to-r from-orange-600/20 to-amber-600/20 p-6 border-b border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-white">{citData.title}</h3>
                          <p className="text-white/60 text-sm mt-1">{citData.descriptionAr || citData.description}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-orange-400 text-sm font-semibold">الاستحقاق: {citData.dueDate || '—'}</span>
                          <span className="block mt-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-orange-500/20 text-orange-300 border border-orange-500/30">{citData.status}</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6 text-center">
                        <FileSpreadsheet size={48} className="mx-auto text-orange-400 mb-4" />
                        <p className="text-white/80 mb-2">الدخل الخاضع: {formatBrandMoney(citData.taxableIncome || 0)}</p>
                        <p className="text-emerald-400 font-bold text-xl mb-4">ضريبة الدخل ({citData.citRate || 20}%): {formatBrandMoney(citData.citDue || 0)}</p>
                        <button type="button" onClick={() => setShowCitForm(true)} className="bg-gradient-to-r from-orange-500 to-amber-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 mx-auto shadow-lg shadow-orange-500/30">
                          <Plus size={18} /><span>إكمال إقرار ضريبة الدخل — CIT</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

      {/* Formulaire Vierge Modal — 4 sections Fuel Purchases pattern */}
      {showFormulaireVierge && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetFormulaireModal}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2"><Download className="h-6 w-6 text-blue-400" />{p('download_blank_form')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="formulaire-vierge-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><Download size={18} />{p('download')}</button>
                <button type="button" onClick={resetFormulaireModal} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1">
              <form id="formulaire-vierge-form" onSubmit={(e) => { e.preventDefault(); logger.log('Formulaire vierge', formulaireFormData); resetFormulaireModal(); }} className="space-y-4">
                <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                  <button type="button" onClick={() => toggleFormulaireSection('selection')} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3"><FileText size={20} className="text-emerald-400" /><h3 className="text-lg font-semibold text-white">{p('form_selection')}</h3></div>
                    {formulaireExpandedSections.selection ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </button>
                  <AnimatePresence>
                    {formulaireExpandedSections.selection && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="md:col-span-2">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('dgi_form')}</label>
                            <CustomSelect value={formulaireFormData.formCode} onChange={(val) => setFormulaireFormData({ ...formulaireFormData, formCode: val })} options={FORM_CODE_SELECT_OPTIONS} placeholder={p('choose_form')} searchable creatable autoSelect={false} />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                  <button type="button" onClick={() => toggleFormulaireSection('format')} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3"><FileSpreadsheet size={20} className="text-blue-400" /><h3 className="text-lg font-semibold text-white">{p('download_format')}</h3></div>
                    {formulaireExpandedSections.format ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </button>
                  <AnimatePresence>
                    {formulaireExpandedSections.format && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('format')} <span className="qawaem-required-mark">*</span></label><CustomSelect value={formulaireFormData.format} onChange={(val) => setFormulaireFormData({ ...formulaireFormData, format: val })} options={formatSelectOptions} placeholder={p('choose_format')} searchable creatable autoSelect={false} /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('language')}</label><CustomSelect value={formulaireFormData.language} onChange={(val) => setFormulaireFormData({ ...formulaireFormData, language: val })} options={languageOptions} placeholder={p('choose_language')} searchable creatable autoSelect={false} /></div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                  <button type="button" onClick={() => toggleFormulaireSection('impression')} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3"><Printer size={20} className="text-yellow-400" /><h3 className="text-lg font-semibold text-white">{p('print_options')}</h3></div>
                    {formulaireExpandedSections.impression ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </button>
                  <AnimatePresence>
                    {formulaireExpandedSections.impression && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="p-4 pt-0"><p className="text-white/60 text-sm">{p('print_options_desc')}</p></div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                  <button type="button" onClick={() => toggleFormulaireSection('notes')} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3"><FileCheck size={20} className="text-purple-400" /><h3 className="text-lg font-semibold text-white">{p('notes')}</h3></div>
                    {formulaireExpandedSections.notes ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </button>
                  <AnimatePresence>
                    {formulaireExpandedSections.notes && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="p-4 pt-0"><textarea value={formulaireFormData.notes} onChange={(e) => setFormulaireFormData({ ...formulaireFormData, notes: e.target.value })} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_notes')} /></div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Nouvelle Déclaration Modal — 4 sections */}
      {showNouvelleDeclaration && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetNouvelleModal}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2"><Plus className="h-6 w-6 text-blue-400" />{p('new_fiscal_declaration')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="nouvelle-declaration-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{p('create')}</button>
                <button type="button" onClick={resetNouvelleModal} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1">
              <form id="nouvelle-declaration-form" onSubmit={handleNouvelleDeclarationSubmit} className="space-y-4">
                <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                  <button type="button" onClick={() => toggleNouvelleSection('typePeriode')} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3"><Receipt size={20} className="text-emerald-400" /><h3 className="text-lg font-semibold text-white">{p('type_period')}</h3></div>
                    {nouvelleExpandedSections.typePeriode ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </button>
                  <AnimatePresence>
                    {nouvelleExpandedSections.typePeriode && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_declaration_code')}</label><input type="text" value={nouvelleFormData.declarationCode} onChange={(e) => setNouvelleFormData({ ...nouvelleFormData, declarationCode: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_declaration_code')} /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('declaration_type')}</label><CustomSelect value={nouvelleFormData.declarationType} onChange={(val) => setNouvelleFormData({ ...nouvelleFormData, declarationType: val })} options={DECL_TYPE_SELECT_OPTIONS} placeholder={p('choose_type')} searchable creatable autoSelect={false} /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('period_required')} />}</label><CustomSelect value={nouvelleFormData.period} onChange={(val) => setNouvelleFormData({ ...nouvelleFormData, period: val })} options={periodSelectOptions} placeholder={p('choose_period')} searchable creatable autoSelect={false} /></div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                  <button type="button" onClick={() => toggleNouvelleSection('identification')} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3"><Building size={20} className="text-blue-400" /><h3 className="text-lg font-semibold text-white">{p('fiscal_identification')}</h3></div>
                    {nouvelleExpandedSections.identification ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </button>
                  <AnimatePresence>
                    {nouvelleExpandedSections.identification && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('fiscal_year')}</label><input type="text" value={nouvelleFormData.fiscalYear} onChange={(e) => setNouvelleFormData({ ...nouvelleFormData, fiscalYear: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('fiscal_year_example')} /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('initial_status')}</label><CustomSelect value={nouvelleFormData.status} onChange={(val) => setNouvelleFormData({ ...nouvelleFormData, status: val })} options={initialStatusOptions} placeholder={p('choose_status')} searchable creatable autoSelect={false} /></div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                  <button type="button" onClick={() => toggleNouvelleSection('optionsCalcul')} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3"><Calculator size={20} className="text-yellow-400" /><h3 className="text-lg font-semibold text-white">{p('calc_options')}</h3></div>
                    {nouvelleExpandedSections.optionsCalcul ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </button>
                  <AnimatePresence>
                    {nouvelleExpandedSections.optionsCalcul && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('auto_calculate')}</label><CustomSelect value={nouvelleFormData.autoCalculate} onChange={(val) => setNouvelleFormData({ ...nouvelleFormData, autoCalculate: val })} options={yesNoOptions} placeholder={p('choose')} searchable creatable autoSelect={false} /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('draft_mode')}</label><CustomSelect value={nouvelleFormData.draftMode} onChange={(val) => setNouvelleFormData({ ...nouvelleFormData, draftMode: val })} options={yesNoOptions} placeholder={p('choose')} searchable creatable autoSelect={false} /></div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                  <button type="button" onClick={() => toggleNouvelleSection('validation')} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3"><Send size={20} className="text-red-400" /><h3 className="text-lg font-semibold text-white">{p('validation_notes')}</h3></div>
                    {nouvelleExpandedSections.validation ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </button>
                  <AnimatePresence>
                    {nouvelleExpandedSections.validation && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="p-4 pt-0"><textarea value={nouvelleFormData.notes} onChange={(e) => setNouvelleFormData({ ...nouvelleFormData, notes: e.target.value })} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('declaration_notes_placeholder')} /></div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Manual VAT declaration modal */}
      {showTVATeledeClarer && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetTvaModal}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2"><FileText className="h-6 w-6 text-blue-400" />{p('tva_teledeclare_title')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="tva-manual-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><Save size={18} />{p('transmit')}</button>
                <button type="button" onClick={resetTvaModal} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1">
              <p className="text-blue-200/80 text-sm mb-4 bg-blue-500/10 border border-blue-400/20 rounded-xl px-4 py-3">{p('manual_filing_hint')}</p>
              <form id="tva-manual-form" onSubmit={handleSaveVatDeclaration} className="space-y-4">
                <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                  <button type="button" onClick={() => toggleTvaSection('periode')} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3"><Calendar size={20} className="text-blue-400" /><h3 className="text-lg font-semibold text-white">{p('period_type')}</h3></div>
                    {tvaExpandedSections.periode ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </button>
                  <AnimatePresence>
                    {tvaExpandedSections.periode && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('period_required')} />}</label><CustomSelect value={tvaFormData.period} onChange={(val) => setTvaFormData({ ...tvaFormData, period: val })} options={periodSelectOptions} placeholder={p('choose_period')} searchable creatable autoSelect={false} /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('declaration_type')}</label><CustomSelect value={tvaFormData.declarationType} onChange={(val) => setTvaFormData({ ...tvaFormData, declarationType: val })} options={vatFilingPeriodOptions} placeholder={p('choose_type')} searchable creatable autoSelect={false} /></div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                  <button type="button" onClick={() => toggleTvaSection('montants')} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3"><DollarSign size={20} className="text-yellow-400" /><h3 className="text-lg font-semibold text-white">{p('tva_amounts')}</h3></div>
                    {tvaExpandedSections.montants ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </button>
                  <AnimatePresence>
                    {tvaExpandedSections.montants && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('tva_collected_da')}</label><CustomNumberInput step={0.01} value={tvaFormData.tvaCollectee} onChange={(v) => setTvaFormData({ ...tvaFormData, tvaCollectee: v })} placeholder="0" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('tva_deductible_da')}</label><CustomNumberInput step={0.01} value={tvaFormData.tvaDeductible} onChange={(v) => setTvaFormData({ ...tvaFormData, tvaDeductible: v })} placeholder="0" /></div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                  <button type="button" onClick={() => toggleTvaSection('notes')} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3"><FileText size={20} className="text-emerald-400" /><h3 className="text-lg font-semibold text-white">{p('transmission')}</h3></div>
                    {tvaExpandedSections.notes ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </button>
                  <AnimatePresence>
                    {tvaExpandedSections.notes && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="p-4 pt-0"><textarea value={tvaFormData.notes} onChange={(e) => setTvaFormData({ ...tvaFormData, notes: e.target.value })} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('transmission_notes')} /></div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      <TVCPManager
        isOpen={showTVCPManager}
        onClose={() => setShowTVCPManager(false)}
        fiscalYearId={fiscalYearId}
      />

      <CorporateIncomeTaxForm
        isOpen={showCitForm}
        onClose={() => setShowCitForm(false)}
        fiscalYearId={fiscalYearId}
      />

      <ZakatDeclarationForm
        isOpen={showZakatForm}
        onClose={() => setShowZakatForm(false)}
      />

      <WithholdingTaxForm
        isOpen={showWhtForm}
        onClose={() => setShowWhtForm(false)}
      />

      {showViewModal && viewingDeclaration ? ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => { setShowViewModal(false); setViewingDeclaration(null); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4)', width: '95%', maxWidth: '900px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <FileText className="h-6 w-6 text-blue-400" />
                {p('action_view')}
              </h2>
              <button type="button" onClick={() => { setShowViewModal(false); setViewingDeclaration(null); }} className="text-white/60 hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><p className="text-white/60 text-xs mb-1">{p('col_declaration_no')}</p><p className="text-white font-semibold">{viewingDeclaration.code || p('na')}</p></div>
              <div><p className="text-white/60 text-xs mb-1">{p('col_label')}</p><p className="text-white font-semibold">{viewingDeclaration.name || p('na')}</p></div>
              <div><p className="text-white/60 text-xs mb-1">{p('col_period')}</p><p className="text-white">{viewingDeclaration.period || p('na')}</p></div>
              <div><p className="text-white/60 text-xs mb-1">{p('col_due_date')}</p><p className="text-white">{viewingDeclaration.dueDate || p('na')}</p></div>
              <div><p className="text-white/60 text-xs mb-1">{p('col_amount')}</p><p className="text-white font-semibold">{formatBrandMoney(viewingDeclaration.amount)}</p></div>
              <div><p className="text-white/60 text-xs mb-1">{p('col_status')}</p><p className="text-white">{viewingDeclaration.status}</p></div>
            </div>
            <button type="button" onClick={() => { setShowViewModal(false); setViewingDeclaration(null); }} className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold border border-white/10">
              {p('close')}
            </button>
          </motion.div>
        </div>,
        document.body
      ) : null}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
      </>
    </div>
  );
};

export { TVCPManager };
export default DeclarationsFiscalesPage;

